"use client"

import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from "react"
import { usePathname } from "next/navigation"
import { useFilterStore } from "@/stores/filterStore"
import { useFilterTransitionStore } from "@/hooks/useFilterStore"
import useLocaleStore from "@/app/stores/useLocaleStore"
import { Accordion, AccordionItem } from "@/components/organisms/Accordion/accordion"
import { AccordionContent, AccordionTrigger } from "@/components/molecules/AccordionItem/accordion-item"
import { PriceFacets } from "@/components/molecules/Facet/price-facet"
import { RatingFacet } from "@/components/molecules/Facet/rating-facet"
import PlaceSearchInput from "@/components/molecules/PlaceSearch"
import { CategoryFacet } from "./facets-category"
import { cn } from "@/utils/index"
import { LocationData } from "@/app/stores/places"

interface FacetsContentProps {
  facetDistribution?: Record<string, any>
  className?: string
  disabledFacets?: string[]
  currentFilters: {
    categories: string[]
    subcategories: string[]
    rating: number
    minPrice: number | null
    maxPrice: number | null
    location?: LocationData | null
  }
  onFilterChange: (filters: Partial<FacetsContentProps["currentFilters"]>) => void
}

const useRoundedRatings = (facetDistribution?: Record<string, any>) => {
  return useMemo(() => {
    const dist = facetDistribution?.["avgRating"] || {}
    const rounded: Record<string, number> = { "2": 0, "3": 0, "4": 0, "5": 0 }

    for (const [key, value] of Object.entries(dist)) {
      const roundedKey = Math.floor(Number(key))
      for (let i = 2; i <= roundedKey; i++) {
        rounded[i] = (rounded[i] || 0) + (value as number)
      }
    }

    return rounded
  }, [facetDistribution])
}

export function FacetsContent({
  facetDistribution,
  className,
  disabledFacets = [],
  currentFilters,
  onFilterChange,
}: FacetsContentProps) {
  const [location, setLocation] = useState<LocationData | null>(null)
  const pathname = usePathname()
  const { set: setFilterVisibilityStatus, status } = useFilterStore()
  const { set: setLastSelected, selected: lastSelected } = useFilterTransitionStore()
  const locale = useLocaleStore((state) => state.locale)
  const roundedRatings = useRoundedRatings(facetDistribution)
  const [isPending, startTransition] = useTransition()

  // Create stable reference to currentFilters
  const currentFiltersRef = useRef(currentFilters)
  useEffect(() => {
    currentFiltersRef.current = currentFilters
  }, [currentFilters])

  // Debounced filter application
  const applyFilters = useCallback((newFilters: Partial<FacetsContentProps["currentFilters"]>) => {
    startTransition(() => {
      onFilterChange(newFilters)
    })
  }, [onFilterChange])

  const handleCategoryChange = useCallback((id: string, isSelected: boolean) => {
    const updated = isSelected
      ? [...currentFiltersRef.current.categories, id]
      : currentFiltersRef.current.categories.filter((cat) => cat !== id)
    applyFilters({ categories: updated })
  }, [applyFilters])

  const handleSubcategoryChange = useCallback((id: string, isSelected: boolean) => {
    const updated = isSelected
      ? [...currentFiltersRef.current.subcategories, id]
      : currentFiltersRef.current.subcategories.filter((sub) => sub !== id)
    applyFilters({ subcategories: updated })
  }, [applyFilters])

  const handleRatingChange = useCallback((checked: boolean, r: string) => {
    const rating = checked ? Number(r) : 0
    applyFilters({ rating })
    setLastSelected("rating")
  }, [applyFilters, setLastSelected])

  const handlePriceChange = useCallback((price: { minPrice: number | null; maxPrice: number | null }) => {
    applyFilters(price)
  }, [applyFilters])

  const resetAllFilters = useCallback(() => {
    startTransition(() => {
      setLocation(null)
      onFilterChange({
        categories: [],
        subcategories: [],
        rating: 0,
        minPrice: null,
        maxPrice: null,
        location: null
      })
    })
  }, [onFilterChange])

  const activeFilterCount = useMemo(() => {
    const { categories, subcategories, minPrice, maxPrice, rating, location } = currentFilters
    return (
      categories.length +
      subcategories.length +
      (location ? 1 : 0) +
      (minPrice ? 1 : 0) +
      (maxPrice ? 1 : 0) +
      (rating ? 1 : 0)
    )
  }, [currentFilters])

  useEffect(() => {
    setFilterVisibilityStatus("idle")
  }, [pathname, setFilterVisibilityStatus])

  const translatedLabels = useMemo(() => ({
    categories: locale === "am" ? "ምድቦች" : "Categories",
    rating: locale === "am" ? "ደረጃ" : "Rating",
    price: locale === "am" ? "ዋጋ" : "Price",
    reset: locale === "am" ? "አጥፉ" : "Reset all filters",
    resetAria: locale === "am" ? "ሁሉንም ማጣሪያዎች ዳግም አስጀምር" : "Reset all filters",
    priceLabels: {
      minPrice: locale === "am" ? "ትንሹ ዋጋ" : "Min Price",
      maxPrice: locale === "am" ? "ትልቁ ዋጋ" : "Max Price",
      minPlaceholder: locale === "am" ? "በትንሹ" : "Min",
      maxPlaceholder: locale === "am" ? "በትልቁ" : "Max",
    },
    location: locale === "am" ? "ቦታ" : "Location"
  }), [locale])

  return (
    <Accordion
      className={cn(
        status === "hidden" && "lg:animate-slide-out-left",
        status === "visible" && "lg:animate-slide-in-left",
        className,
        "dark:AccordionRoot dark:px-2"
      )}
      type="multiple"
      defaultValue={lastSelected}
    >
      {activeFilterCount > 0 && (
        <button
          className="mt-10 inline-flex text-[15px] text-black underline dark:text-gray-200"
          onClick={resetAllFilters}
          aria-label={translatedLabels.resetAria}
          disabled={isPending}
        >
          {translatedLabels.reset} ({activeFilterCount})
        </button>
      )}

      {!disabledFacets.includes("location") && (
        <AccordionItem value="location">
          <AccordionTrigger className="p-2 text-base">
            {translatedLabels.location}
          </AccordionTrigger>
          <AccordionContent className="px-2">
            <PlaceSearchInput
              borderless={false}
              value={location?.address ?? ""}
              onChange={(newLocation) => {
                setLocation(newLocation as LocationData)
                applyFilters({ location: newLocation as LocationData })
              }}
              className="relative z-0"
            />
          </AccordionContent>
        </AccordionItem>
      )}

      {!disabledFacets.includes("categories") && (
        <CategoryFacet
          id="categories"
          title={translatedLabels.categories}
          selectedCategories={currentFilters.categories}
          selectedSubcategories={currentFilters.subcategories}
          onCategoryChange={handleCategoryChange}
          onSubcategoryChange={handleSubcategoryChange}
        />
      )}

      {!disabledFacets.includes("avgRating") && (
        <RatingFacet
          id="avgRating"
          title={translatedLabels.rating}
          distribution={roundedRatings}
          isChecked={(r) => currentFilters.rating === Number(r)}
          onCheckedChange={handleRatingChange}
        />
      )}

      {!disabledFacets.includes("price") && (
        <AccordionItem value="price">
          <AccordionTrigger className="p-2 text-base">
            {translatedLabels.price}
          </AccordionTrigger>
          <AccordionContent className="px-2">
            <PriceFacets
              initMin={currentFilters.minPrice}
              initMax={currentFilters.maxPrice}
              labels={translatedLabels.priceLabels}
              setFacets={handlePriceChange}
              disabled={isPending}
            />
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  )
}